import { useEffect, useRef, useState } from "react";
import { inputClass } from "@/components/manufacturing/manufacturingSurfaceClasses";

type Props = {
  label: string;
  options: readonly string[];
  selected: readonly string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  emptyLabel?: string;
};

export function PricingFacetMultiSelect({
  label,
  options,
  selected,
  onChange,
  disabled,
  emptyLabel = "All",
}: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const selectedSet = new Set(selected);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const summary =
    selected.length === 0
      ? emptyLabel
      : selected.length === 1
        ? selected[0]
        : `${selected.length} selected`;

  const toggle = (value: string) => {
    if (selectedSet.has(value)) onChange(selected.filter((v) => v !== value));
    else onChange([...selected, value]);
  };

  return (
    <div ref={rootRef} className="relative block min-w-[8rem] flex-1 space-y-0.5 text-sm sm:max-w-[12rem]">
      <span className="text-xs text-gray-600 dark:text-zinc-400">{label}</span>
      <button
        type="button"
        className={`${inputClass} flex w-full items-center justify-between gap-2 text-left`}
        onClick={() => setOpen((v) => !v)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className="truncate">{summary}</span>
        <span className="text-[10px] text-gray-400">▾</span>
      </button>
      {open ? (
        <div className="absolute z-30 mt-1 max-h-56 w-full min-w-[12rem] overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-zinc-700 dark:bg-zinc-950">
          {options.length === 0 ? (
            <div className="px-3 py-2 text-xs text-gray-500 dark:text-zinc-400">No values yet</div>
          ) : (
            <ul role="listbox" aria-multiselectable className="text-sm">
              {options.map((opt) => {
                const checked = selectedSet.has(opt);
                return (
                  <li key={opt}>
                    <label className="flex cursor-pointer items-center gap-2 px-3 py-1.5 hover:bg-gray-50 dark:hover:bg-zinc-900">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggle(opt)}
                        disabled={disabled}
                      />
                      <span className="truncate">{opt}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          {selected.length > 0 ? (
            <button
              type="button"
              className="w-full border-t border-gray-100 px-3 py-1.5 text-left text-xs text-indigo-700 hover:bg-gray-50 dark:border-zinc-800 dark:text-indigo-300 dark:hover:bg-zinc-900"
              onClick={() => onChange([])}
            >
              Clear {label.toLowerCase()}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
